import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Settings, Image as ImgIcon, Bell, Users as UsersIcon, Briefcase,
  Loader2, Plus, Pencil, Trash2, Copy, Check, KeyRound, ExternalLink,
  Upload, Building2,
} from "lucide-react";
import { toast } from "sonner";

const PORTAL_TOKEN_KEY = "dnf_portal_token";

function api() {
  const t = sessionStorage.getItem(PORTAL_TOKEN_KEY);
  return axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    headers: t ? { Authorization: `Bearer ${t}` } : {},
  });
}

// ============================================================
// Componente raíz: PortalConfiguracion (4 sub-tabs)
// ============================================================
export function PortalConfiguracion({ cliente, onClienteUpdated }) {
  const [subTab, setSubTab] = useState("perfil");
  const TABS = [
    { id: "perfil",       label: "Perfil & Logo",  icon: ImgIcon },
    { id: "notificaciones", label: "Notificaciones", icon: Bell },
    { id: "contactos",    label: "Contactos",      icon: UsersIcon },
    { id: "subportales",  label: "Sub-portales",   icon: Briefcase },
  ];
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden" data-testid="portal-config">
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <Settings className="w-4 h-4 text-amber-400" />
        <h2 className="font-semibold text-sm">Configuración del portal</h2>
      </div>
      <div className="flex gap-1 p-2 bg-zinc-950/40 border-b border-zinc-800 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setSubTab(t.id)}
            data-testid={`portal-config-tab-${t.id}`}
            className={`px-3 py-1.5 text-xs rounded inline-flex items-center gap-1.5 whitespace-nowrap ${
              subTab === t.id ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-200"
            }`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>
      <div className="p-4 sm:p-5 space-y-4">
        {subTab === "perfil" && <SecPerfil cliente={cliente} onClienteUpdated={onClienteUpdated} />}
        {subTab === "notificaciones" && <SecNotificaciones />}
        {subTab === "contactos" && <SecContactos />}
        {subTab === "subportales" && <SecSubPortales cliente={cliente} />}
      </div>
    </div>
  );
}

// ============================================================
// Sección 1: Perfil & Logo
// ============================================================
function SecPerfil({ cliente, onClienteUpdated }) {
  const [form, setForm] = useState({
    logo_url: cliente?.logo_url || "",
    web: cliente?.web || "",
    telefono: cliente?.telefono || "",
    email_contacto: cliente?.email_contacto || "",
    persona_contacto: cliente?.persona_contacto || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const subirLogo = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("El logo debe pesar menos de 5 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api().post("/portal/cliente/upload-logo", fd);
      setForm((f) => ({ ...f, logo_url: data.secure_url }));
      toast.success("Logo subido · pulsa Guardar para aplicar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo logo");
    } finally {
      setUploading(false);
    }
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api().patch("/portal/cliente/profile", form);
      toast.success("Perfil actualizado");
      if (onClienteUpdated) onClienteUpdated(data.cliente);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando perfil");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sec-perfil">
      <p className="text-xs text-zinc-400">
        Tu logo aparece en tu portal y en los documentos compartidos con DNF.
      </p>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="shrink-0">
          <div className="w-32 h-32 bg-white rounded-lg border-2 border-dashed border-zinc-700 flex items-center justify-center overflow-hidden">
            {form.logo_url ? (
              <img src={form.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="logo-preview" />
            ) : (
              <ImgIcon className="w-10 h-10 text-zinc-400" />
            )}
          </div>
          <label className="mt-2 block">
            <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden"
              onChange={(e) => subirLogo(e.target.files?.[0])}
              data-testid="logo-input" />
            <Button asChild variant="outline" size="sm" className="w-full" disabled={uploading}>
              <span>
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                {form.logo_url ? "Cambiar logo" : "Subir logo"}
              </span>
            </Button>
          </label>
          {form.logo_url && (
            <button onClick={() => setForm((f) => ({ ...f, logo_url: "" }))}
              data-testid="btn-quitar-logo"
              className="mt-1 text-[11px] text-zinc-500 hover:text-red-400 w-full text-center">
              Quitar
            </button>
          )}
        </div>
        <div className="flex-1 min-w-[240px] grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Persona de contacto</Label>
            <Input value={form.persona_contacto} onChange={(e) => setForm({ ...form, persona_contacto: e.target.value })}
              data-testid="input-persona-contacto" className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Email principal</Label>
            <Input type="email" value={form.email_contacto} onChange={(e) => setForm({ ...form, email_contacto: e.target.value })}
              data-testid="input-email-contacto" className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Teléfono</Label>
            <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              data-testid="input-telefono" className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Web</Label>
            <Input value={form.web} onChange={(e) => setForm({ ...form, web: e.target.value })}
              data-testid="input-web" placeholder="https://..." className="bg-zinc-900 border-zinc-800 text-sm" />
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={guardar} disabled={saving} data-testid="btn-guardar-perfil"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
          Guardar cambios
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Sección 2: Notificaciones
// ============================================================
function SecNotificaciones() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api().get("/portal/cliente/notif-prefs");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando notificaciones");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleCanal = (eventKey, canal) => {
    setData((d) => {
      const next = JSON.parse(JSON.stringify(d));
      next.prefs.canales[eventKey][canal] = !next.prefs.canales[eventKey][canal];
      return next;
    });
  };

  const guardar = async () => {
    setSaving(true);
    try {
      await api().patch("/portal/cliente/notif-prefs", {
        canales: data.prefs.canales,
        resumen_diario: data.prefs.resumen_diario,
        hora_resumen: data.prefs.hora_resumen,
        estampar_fotos: data.prefs.estampar_fotos !== false,
      });
      toast.success("Preferencias guardadas");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="sec-notif">
      <p className="text-xs text-zinc-400">Elige qué notificaciones quieres recibir y por qué canales.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500 border-b border-zinc-800">
              <th className="px-2 py-2">Evento</th>
              <th className="px-2 py-2 text-center w-24">📧 Email</th>
              <th className="px-2 py-2 text-center w-24">💬 WhatsApp</th>
            </tr>
          </thead>
          <tbody>
            {data.events.map((ev) => {
              const c = data.prefs.canales[ev.key] || { email: false, whatsapp: false };
              return (
                <tr key={ev.key} className="border-b border-zinc-800/40" data-testid={`row-evento-${ev.key}`}>
                  <td className="px-2 py-2.5 text-zinc-200">{ev.label}</td>
                  <td className="px-2 py-2.5 text-center">
                    <Switch checked={c.email} onCheckedChange={() => toggleCanal(ev.key, "email")}
                      data-testid={`switch-${ev.key}-email`} />
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <Switch checked={c.whatsapp} onCheckedChange={() => toggleCanal(ev.key, "whatsapp")}
                      data-testid={`switch-${ev.key}-whatsapp`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="bg-zinc-950/40 border border-zinc-800 rounded p-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-zinc-200">📸 Timbrar fotos de los reportes</div>
            <div className="text-[11px] text-zinc-500">Estampar fecha/hora y ubicación sobre las fotos de montaje. Desactívalo si prefieres las fotos limpias.</div>
          </div>
          <Switch checked={data.prefs.estampar_fotos !== false}
            onCheckedChange={(v) => setData((d) => ({ ...d, prefs: { ...d.prefs, estampar_fotos: v } }))}
            data-testid="switch-estampar-fotos" />
        </div>
      </div>
      <div className="bg-zinc-950/40 border border-zinc-800 rounded p-3 space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs font-semibold text-zinc-200">Resumen diario por email</div>
            <div className="text-[11px] text-zinc-500">Agrupar todos los emails del día en 1 solo aviso</div>
          </div>
          <Switch checked={!!data.prefs.resumen_diario}
            onCheckedChange={(v) => setData((d) => ({ ...d, prefs: { ...d.prefs, resumen_diario: v } }))}
            data-testid="switch-resumen-diario" />
        </div>
        {data.prefs.resumen_diario && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-zinc-400">Hora de envío:</Label>
            <Input type="time" value={data.prefs.hora_resumen || "19:00"}
              onChange={(e) => setData((d) => ({ ...d, prefs: { ...d.prefs, hora_resumen: e.target.value } }))}
              data-testid="input-hora-resumen"
              className="bg-zinc-900 border-zinc-800 w-28" />
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button onClick={guardar} disabled={saving} data-testid="btn-guardar-notif"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
          Guardar preferencias
        </Button>
      </div>
    </div>
  );
}

// ============================================================
// Sección 3: Contactos
// ============================================================
function SecContactos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | contacto
  const [form, setForm] = useState({ nombre: "", rol: "", email: "", telefono: "", notas: "", principal: false });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api().get("/portal/cliente/contactos");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando contactos");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const abrirNuevo = () => {
    setForm({ nombre: "", rol: "", email: "", telefono: "", notas: "", principal: false });
    setEditing("new");
  };
  const abrirEditar = (c) => {
    setForm({
      nombre: c.nombre || "", rol: c.rol || "", email: c.email || "",
      telefono: c.telefono || "", notas: c.notas || "", principal: !!c.principal,
    });
    setEditing(c);
  };
  const cerrar = () => setEditing(null);

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    try {
      if (editing === "new") {
        await api().post("/portal/cliente/contactos", form);
        toast.success("Contacto añadido");
      } else {
        await api().patch(`/portal/cliente/contactos/${editing.id}`, form);
        toast.success("Contacto actualizado");
      }
      cerrar();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    }
  };

  const eliminar = async (c) => {
    if (!confirm(`¿Eliminar contacto "${c.nombre}"?`)) return;
    try {
      await api().delete(`/portal/cliente/contactos/${c.id}`);
      toast.success("Contacto eliminado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error eliminando");
    }
  };

  return (
    <div className="space-y-3" data-testid="sec-contactos">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-zinc-400">Personas de tu equipo que pueden ser CC en notificaciones o referencia para DNF.</p>
        <Button onClick={abrirNuevo} size="sm" data-testid="btn-nuevo-contacto"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo contacto
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-zinc-500 italic py-8 text-sm border border-dashed border-zinc-800 rounded">
          Aún no has añadido contactos
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`contacto-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-zinc-100">{c.nombre}</span>
                  {c.principal && <span className="text-[10px] bg-amber-500/30 text-amber-200 px-1.5 py-0.5 rounded uppercase">principal</span>}
                  {c.rol && <span className="text-[10px] bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">{c.rol}</span>}
                </div>
                <div className="text-xs text-zinc-400 mt-0.5 truncate">
                  {c.email && <span>📧 {c.email}</span>}
                  {c.email && c.telefono && <span className="mx-2">·</span>}
                  {c.telefono && <span>📞 {c.telefono}</span>}
                </div>
                {c.notas && <div className="text-[11px] text-zinc-500 mt-0.5 italic truncate">{c.notas}</div>}
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => abrirEditar(c)} data-testid={`btn-editar-contacto-${c.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => eliminar(c)} data-testid={`btn-eliminar-contacto-${c.id}`}
                  className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) cerrar(); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="dialog-contacto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nuevo contacto" : "Editar contacto"}</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              La información se guarda en la ficha de tu cliente y queda visible para DNF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                data-testid="form-contacto-nombre" className="bg-zinc-900 border-zinc-800" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Rol</Label>
                <Input value={form.rol} onChange={(e) => setForm({ ...form, rol: e.target.value })}
                  placeholder="comercial, admin, diseño..."
                  data-testid="form-contacto-rol" className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs">Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  data-testid="form-contacto-tel" className="bg-zinc-900 border-zinc-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="form-contacto-email" className="bg-zinc-900 border-zinc-800" />
            </div>
            <div>
              <Label className="text-xs">Notas</Label>
              <Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })}
                rows={2} data-testid="form-contacto-notas" className="bg-zinc-900 border-zinc-800 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={form.principal}
                onChange={(e) => setForm({ ...form, principal: e.target.checked })}
                data-testid="form-contacto-principal" />
              Marcar como contacto principal
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cerrar}>Cancelar</Button>
            <Button onClick={guardar} data-testid="btn-guardar-contacto"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================
// Sección 4: Sub-portales (white-label)
// ============================================================
function SecSubPortales({ cliente }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [usersDialog, setUsersDialog] = useState(null); // sub-portal abierto en panel usuarios
  const [newPassword, setNewPassword] = useState(null); // { user, password }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api().get("/portal/cliente/sub-portales");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando sub-portales");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const desactivar = async (sp) => {
    if (!confirm(`¿Desactivar el sub-portal "${sp.nombre}"? Los usuarios no podrán acceder.`)) return;
    try {
      await api().delete(`/portal/cliente/sub-portales/${sp.id}`);
      toast.success("Sub-portal desactivado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error desactivando");
    }
  };

  return (
    <div className="space-y-3" data-testid="sec-subportales">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-xs text-zinc-400">Crea portales independientes para tus propias marcas/clientes finales.</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">Cada sub-portal tiene su propio login y un branding personalizado.</p>
        </div>
        <Button onClick={() => setEditing({ nuevo: true, nombre: "", color_primario: "#f59e0b", espacios_ids: [], cadenas_ids: [] })}
          size="sm" data-testid="btn-nuevo-subportal"
          className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nuevo sub-portal
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <div className="text-center text-zinc-500 italic py-8 text-sm border border-dashed border-zinc-800 rounded">
          Aún no has creado sub-portales
        </div>
      ) : (
        <div className="space-y-2">
          {items.filter((s) => s.activo !== false).map((sp) => (
            <div key={sp.id}
              className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap"
              data-testid={`subportal-${sp.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded bg-white flex items-center justify-center shrink-0 overflow-hidden">
                  {sp.logo_url ? (
                    <img src={sp.logo_url} alt={sp.nombre} className="max-w-full max-h-full object-contain" />
                  ) : (
                    <Building2 className="w-5 h-5 text-zinc-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-zinc-100">{sp.nombre}</span>
                    <span className="text-[10px] font-mono bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded">{sp.slug}</span>
                    <span className="text-[10px] bg-zinc-800 text-zinc-300 px-1.5 py-0.5 rounded">
                      {sp.n_usuarios || 0} usuario{sp.n_usuarios !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-2">
                    <span>{sp.espacios_ids?.length || 0} espacios</span>
                    <a href={`/portal-sub/${sp.slug}`} target="_blank" rel="noreferrer"
                      className="text-amber-400 hover:text-amber-300 inline-flex items-center gap-0.5">
                      <ExternalLink className="w-3 h-3" /> Abrir login
                    </a>
                  </div>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" onClick={() => setUsersDialog(sp)}
                  data-testid={`btn-usuarios-${sp.id}`}>
                  <UsersIcon className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(sp)} data-testid={`btn-editar-subportal-${sp.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => desactivar(sp)}
                  data-testid={`btn-eliminar-subportal-${sp.id}`}
                  className="text-red-400 hover:text-red-300">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <SubPortalDialog editing={editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
      {usersDialog && (
        <SubPortalUsersDialog sp={usersDialog} onClose={() => { setUsersDialog(null); setNewPassword(null); }}
          newPassword={newPassword} setNewPassword={setNewPassword} onUserAdded={load} />
      )}
    </div>
  );
}

function SubPortalDialog({ editing, onClose, onSaved }) {
  const isNew = !!editing.nuevo;
  const [form, setForm] = useState({
    nombre: editing.nombre || "",
    color_primario: editing.color_primario || "#f59e0b",
    logo_url: editing.logo_url || "",
    espacios_ids: editing.espacios_ids || [],
  });
  const [espacios, setEspacios] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api().get("/portal/cliente/espacios").then(({ data }) => setEspacios(data.espacios || [])).catch(() => {});
  }, []);

  const toggleEspacio = (eid) => {
    setForm((f) => {
      const has = f.espacios_ids.includes(eid);
      return { ...f, espacios_ids: has ? f.espacios_ids.filter((x) => x !== eid) : [...f.espacios_ids, eid] };
    });
  };

  const subirLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "dnf/portal-clientes/subportales");
      const { data } = await api().post("/portal/cliente/upload-logo", fd);
      setForm((f) => ({ ...f, logo_url: data.secure_url }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error subiendo logo");
    } finally {
      setUploading(false);
    }
  };

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error("Nombre obligatorio"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api().post("/portal/cliente/sub-portales", form);
        toast.success(`Sub-portal "${data.sub_portal.nombre}" creado · slug: ${data.sub_portal.slug}`);
      } else {
        await api().patch(`/portal/cliente/sub-portales/${editing.id}`, form);
        toast.success("Sub-portal actualizado");
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="dialog-subportal">
        <DialogHeader>
          <DialogTitle>{isNew ? "Crear sub-portal" : `Editar sub-portal · ${editing.nombre}`}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Tus clientes finales verán solo los montajes de los espacios que selecciones aquí.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <div className="flex gap-3 items-start">
            <div className="w-20 h-20 bg-white rounded border-2 border-dashed border-zinc-700 flex items-center justify-center overflow-hidden shrink-0">
              {form.logo_url ? <img src={form.logo_url} alt="" className="max-w-full max-h-full object-contain" /> : <Building2 className="w-7 h-7 text-zinc-400" />}
            </div>
            <div className="flex-1 space-y-2">
              <div>
                <Label className="text-xs">Nombre *</Label>
                <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="form-sp-nombre" className="bg-zinc-900 border-zinc-800" />
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs">Color primario</Label>
                  <Input type="color" value={form.color_primario}
                    onChange={(e) => setForm({ ...form, color_primario: e.target.value })}
                    data-testid="form-sp-color" className="bg-zinc-900 border-zinc-800 h-9 w-full" />
                </div>
                <label>
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => subirLogo(e.target.files?.[0])} data-testid="form-sp-logo-input" />
                  <Button asChild variant="outline" size="sm" disabled={uploading}>
                    <span>{uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Logo"}</span>
                  </Button>
                </label>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Espacios visibles ({form.espacios_ids.length} de {espacios.length})</Label>
            <p className="text-[11px] text-zinc-500 mb-2">Si no marcas ninguno, el sub-portal no verá nada hasta que selecciones.</p>
            <div className="border border-zinc-800 rounded p-2 max-h-48 overflow-y-auto space-y-1">
              {espacios.length === 0 && <div className="text-xs text-zinc-500 italic text-center py-2">No tienes espacios asociados</div>}
              {espacios.map((e) => (
                <label key={e.id} className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer hover:bg-zinc-800/40 px-1.5 py-1 rounded"
                  data-testid={`espacio-check-${e.id}`}>
                  <input type="checkbox" checked={form.espacios_ids.includes(e.id)}
                    onChange={() => toggleEspacio(e.id)} />
                  <span className="flex-1 truncate">{e.nombre}</span>
                  {e.ciudad && <span className="text-[10px] text-zinc-500">{e.ciudad}</span>}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="btn-guardar-subportal"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
            {isNew ? "Crear" : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubPortalUsersDialog({ sp, onClose, newPassword, setNewPassword, onUserAdded }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ email: "", nombre: "" });
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api().get(`/portal/cliente/sub-portales/${sp.id}/usuarios`);
      setUsers(data.items || []);
    } catch (e) {
      toast.error("Error cargando usuarios");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [sp.id]);

  const crearUser = async () => {
    if (!form.email || !form.nombre) { toast.error("Email y nombre obligatorios"); return; }
    setCreating(true);
    try {
      const { data } = await api().post(`/portal/cliente/sub-portales/${sp.id}/usuarios`, form);
      setNewPassword({ user: data.usuario, password: data.password_temporal });
      setForm({ email: "", nombre: "" });
      toast.success("Usuario creado");
      onUserAdded();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setCreating(false);
    }
  };

  const eliminarUser = async (u) => {
    if (!confirm(`¿Eliminar acceso de "${u.email}"?`)) return;
    try {
      await api().delete(`/portal/cliente/sub-portales/usuarios/${u.id}`);
      toast.success("Usuario eliminado");
      await load();
    } catch (e) {
      toast.error("Error eliminando");
    }
  };

  const copiar = (txt) => {
    navigator.clipboard.writeText(txt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg" data-testid="dialog-subportal-users">
        <DialogHeader>
          <DialogTitle>Usuarios · {sp.nombre}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            URL: <code className="text-amber-300">/portal-sub/{sp.slug}</code>
          </DialogDescription>
        </DialogHeader>
        {newPassword && (
          <div className="bg-emerald-500/15 border border-emerald-500/40 rounded p-3 mb-3" data-testid="new-password-box">
            <div className="flex items-center gap-2 text-emerald-300 mb-2">
              <KeyRound className="w-4 h-4" />
              <span className="text-sm font-semibold">Usuario creado · guarda la contraseña ahora</span>
            </div>
            <div className="text-xs text-zinc-300 mb-1">Email: <span className="font-mono">{newPassword.user.email}</span></div>
            <div className="flex items-center gap-2">
              <code className="bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-amber-300 font-mono text-sm flex-1">{newPassword.password}</code>
              <Button size="sm" variant="outline" onClick={() => copiar(newPassword.password)} data-testid="btn-copiar-password">
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <div className="text-[11px] text-amber-200 mt-1.5">⚠️ No volveremos a mostrar esta contraseña.</div>
          </div>
        )}
        <div className="space-y-3 max-h-[40vh] overflow-y-auto">
          <div className="bg-zinc-950 border border-zinc-800 rounded p-3 space-y-2">
            <div className="text-xs font-semibold text-zinc-200">Crear nuevo acceso</div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Nombre" data-testid="form-newuser-nombre" className="bg-zinc-900 border-zinc-800 text-sm" />
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="email@cliente.com" data-testid="form-newuser-email" className="bg-zinc-900 border-zinc-800 text-sm" />
            </div>
            <Button onClick={crearUser} disabled={creating} size="sm" data-testid="btn-crear-user"
              className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold w-full">
              {creating && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />}
              <Plus className="w-3.5 h-3.5 mr-1" /> Generar contraseña y crear
            </Button>
          </div>
          <div className="text-xs font-semibold text-zinc-400 px-1">Usuarios actuales ({users.length})</div>
          {loading ? (
            <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          ) : users.length === 0 ? (
            <div className="text-xs text-zinc-500 italic text-center py-4">Aún no hay usuarios</div>
          ) : users.map((u) => (
            <div key={u.id} className="bg-zinc-950 border border-zinc-800 rounded p-2 flex items-center justify-between gap-2"
              data-testid={`user-${u.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-200 truncate">{u.nombre}</div>
                <div className="text-[11px] text-zinc-500 truncate">{u.email} · {u.last_login ? `último login: ${new Date(u.last_login).toLocaleDateString()}` : "sin login todavía"}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => eliminarUser(u)}
                data-testid={`btn-eliminar-user-${u.id}`}
                className="text-red-400 hover:text-red-300">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
